import axios from "axios";
import config from "@/config/config"
import * as constants from "@/config/constants";

function createUrl(segments, params) {
    let url = config.getPGWYBaseUrl();

    if (segments) {
        url += (url.endsWith("/") ? "" : "/") +
            segments.map((segment) => encodeURIComponent(segment)).join("/");
    }

    if (params) {
        url += "?" + Object.entries(params)
            .map(([key, value]) =>
                encodeURIComponent(key) + "=" +
                (value == null ? "" : encodeURIComponent(String(value))))
            .join("&");
    }
    return url;
}

function get(segments, options = {}) {
    return request("get", segments, options);
}

function post(segments, options = {}) {
    return request("post", segments, options);
}

async function signAndResend(method, segments, options, signatureId) {
    await pgwyClient.sign(signatureId);
    if (options.data instanceof FormData) {
        options.data.append("signatureId", signatureId);
    } else if (options.data) {
        options.data = {...options.data, signatureId};
    } else if (options.params) {
        options.params = {...options.params, signatureId};
    } else {
        options.params = {signatureId};
    }
    options.isSigned = true;
    return await request(method, segments, options);
}

// {params, data, headers, responseType = "json", isSigned, maxRedirects}
function request(method, segments, options = {}) {
    return new Promise( (resolve, reject) => {
        const url = createUrl(segments, options.params);
        axios.request({
            method,
            url,
            headers: options.headers,
            data: options.data,
            responseType: options.responseType
        }).then((res) => {
            if (res.data?.error === -19 && res.data?.signatureId && !options.isSigned) {
                signAndResend(method, segments, options, res.data.signatureId)
                    .then(resolve, reject);
            } else if (res.data?.error) {
                reject(responseError("CM responded with error code "+ res.data.error + ": " + res.data.msg, res));
            } else {
                resolve(res);
            }
        }).catch(async (err) => {
            if (err.response?.status === 404) {
                console.log("There is a 404 response on " + url + ". Make sure that the corresponding endpoint is enabled in Protocol Gateway.");
                reject(err);
            } else if (err.response?.status === 401) {
                window.location = constants.WEB_UI_ROOT + constants.LOGOUT_ROUTE_PATH;
            } else {
                reject(err);
            }
        });
    });
}

function parseSemVerVersion(version) {
    return version.split(".")
        .map((v) => parseInt(v))
        .filter((v) => !isNaN(v));
}

function isAtLeastVersion(version, atLeastVersion) {
    const versionParts = parseSemVerVersion(version);
    const atLeastVersionParts = parseSemVerVersion(atLeastVersion);

    for (let i = 0; i < atLeastVersionParts.length; i++) {
        let versionPart = versionParts.length > i ? versionParts[i] : 0;

        if (versionPart === atLeastVersionParts[i]) {
            continue;
        }

        return versionPart > atLeastVersionParts[i];
    }
    return true;
}

function responseError(msg, response) {
    const error = new Error(msg);
    error.response = response;
    return error;
}

const pgwyClient = {
    sign(signatureId) {
        return new Promise((resolve, reject) => {
            window.open(createUrl(["auth", "sign"], {signatureId}), "_self");
            function refresh() {
                get(["auth", "sign-status"],{params: {signatureId}})
                    .then((statusResponse) => {
                        if (statusResponse.data?.status === "signed") {
                            resolve(statusResponse);
                        } else if (statusResponse.data?.status !== "pending") {
                            reject(responseError("Signing failed with status " + statusResponse.data?.status, statusResponse));
                        } else {
                            setTimeout(refresh, 1000);
                        }
                    }).catch (reject);
            }
            setTimeout(refresh, 1000);
        });
    },
    login() {
        return new Promise((resolve, reject) => {
            window.open(createUrl(["auth", "login"]), "_self");
            function refresh(isInitiated) {
                pgwyClient.getStatus().then((statusResponse) => {
                    if (statusResponse.data.status === "loggedin") {
                        resolve(statusResponse);
                    } else if (
                        statusResponse.data.status === "cancelled" ||
                        statusResponse.data.status === "notaccepted" ||
                        (statusResponse.data.status === "loggedout" && isInitiated)) {
                        reject(responseError("Login failed", statusResponse));
                    } else {
                        setTimeout(() => {
                            refresh(isInitiated || statusResponse.data.status === "pending")
                        }, 1000);
                    }
                }).catch(reject);
            }
            setTimeout(() => { refresh(false) }, 1000);
        });
    },
    logout() {
        return get(["auth", "logout"]);
    },
    getStatus() {
        return new Promise((resolve, reject) => {
            get(["auth", "status"])
                .then((res) => {
                    const cmVersion = res.data?.version;
                    const status = res.data?.status;
                    const minWebUIVersion = res.data?.minWebUIVersion;
                    if ((!cmVersion || !status) && config.switchToNextFallbackPgwyBaseUrl() ) {
                        // The response is not a valid status response. Try switching to next pgwy url.
                        pgwyClient.getStatus().then(resolve).catch(reject);
                    } else if (!isAtLeastVersion(cmVersion, constants.MINIMUM_CM_VERSION)) {
                        const msg = "Version incompatibility. CM Web UI version " + constants.WEB_UI_VERSION + " requires CM version to be at least " + constants.MINIMUM_CM_VERSION;
                        alert(msg);
                        reject(responseError(msg, res));
                    } else if (minWebUIVersion && !isAtLeastVersion(constants.WEB_UI_VERSION, minWebUIVersion)) {
                        const msg = "Version incompatibility. CM version " + cmVersion + " requires CM Web UI version to be at least " + minWebUIVersion;
                        alert(msg);
                        reject(responseError(msg, res));
                    } else {
                        resolve(res);
                    }
                }).catch((err) => {
                    if ((!err.response || err.response?.status === 404) && config.switchToNextFallbackPgwyBaseUrl()) {
                        // Not found. Try switching to next pgwy url.
                        pgwyClient.getStatus().then(resolve).catch(reject);
                    } else {
                        reject(err);
                    }
                });
        });
    },
    async getOfficerDetails() {
        const response = await get(["auth", "officer"]);
        if (response.data.officer) {
            // New version of the response wraps all data in a subnode named officer:
            response.data = response.data.officer;
        }
        return response;
    },
    getIssuers(args) {
        return get(["api", "certificates", "issuers"], {params: args});
    },
    getCertificates(args) {
        return get(["api", "certificates"], {params: args});
    },
    revokeCertificates(certIds, reason) {
        return post(["api", "certificates", "revoke"], {
            params: {certid: certIds.join(","), reason: reason}
        });
    },
    reinstateCertificates(certIds) {
        return post(["api", "certificates", "reinstate"], {
            params: {certid: certIds.join(",")}
        });
    },
    async downloadCertificates(certIds, type, extension) {
        const response = await get(["api", "certificates", "download"], {
            headers: {accept: "application/" + type},
            params: {certid: certIds.join(",")},
            responseType: "blob"
        });
        const blob = response.data;
        const link = document.createElement("a");
        link.setAttribute("href", URL.createObjectURL(blob));
        link.setAttribute("download",
            certIds.length === 1 ? certIds[0] + extension : "certificates.zip");
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },
    getProcedures() {
        return get(["api", "procedures"]);
    },
    getProcedureDetails(procedureId) {
        return get(["api", "procedures", procedureId, "details"]);
    },
    createPkcs10Certificate(pkcs10, procname, args) {
        const data = new FormData();
        data.append("pkcs10", pkcs10);
        data.append("procname", procname);
        Object.entries(args).forEach(([key, value]) => {
            data.append(key, String(value));
        })
        return post(["api", "certificates", "pkcs10"], {data});
    },
    getStatistics(type, args) {
        return get(["api", "statistics", type], {params: args});
    },
    getRegistrations(procid, protocol, args) {
        return get(["api", "registrations", procid, protocol.toLowerCase()], {params: args});
    },
    createRegistration(procid, protocol, args) {
        return post(["api", "registrations", procid, protocol], {data: args});
    },
    updateRegistration(procid, protocol, args) {
        return request("put", ["api", "registrations", procid, protocol], {data: args});
    }
};

export default pgwyClient;